import React, { useEffect, useRef } from "react";

const HIGHLIGHT_CLASS = "comment-highlight";
const HIGHLIGHT_DURATION_MS = 3000;

interface CommentHighlightProps {
  active: boolean;
  children: React.ReactNode;
}

/**
 * CommentHighlight wraps content that should be visually highlighted when
 * navigating to a specific comment via a deep link.
 *
 * When `active` becomes true:
 * - Scrolls the element into view (smooth, centered)
 * - Applies the `comment-highlight` CSS class for 3 seconds
 */
export const CommentHighlight: React.FC<CommentHighlightProps> = ({
  active,
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active || !ref.current) return;

    ref.current.classList.add(HIGHLIGHT_CLASS);
    ref.current.scrollIntoView({ behavior: "smooth", block: "center" });

    const timer = setTimeout(() => {
      ref.current?.classList.remove(HIGHLIGHT_CLASS);
    }, HIGHLIGHT_DURATION_MS);

    return () => {
      clearTimeout(timer);
      ref.current?.classList.remove(HIGHLIGHT_CLASS);
    };
  }, [active]);

  return <div ref={ref}>{children}</div>;
};
